import Task from './Task'
import store from '@/store'
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import Application from 'App/Foundation/Application'

/**
 * Get the API Client instance from the container.
 *
 * @return {Client}
 */
export const api = () => Application.getInstance().api()

/**
 * Set the bearer token Echo/Pusher sends when authorizing private channels.
 *
 * @param {Echo} echo
 * @param {String} token
 * @return {void}
 */
const setAuthToken = (echo, token) => {
	if (!token || !echo?.connector) {
		return
	}

	const bearerToken = `Bearer ${token}`
	if (echo.connector.options?.auth?.headers) {
		echo.connector.options.auth.headers.Authorization = bearerToken
	}
	if (echo.connector.pusher?.config?.auth?.headers) {
		echo.connector.pusher.config.auth.headers.Authorization = bearerToken
	}
}

/**
 * Establish a websocket connection once authenticated.
 *
 * @author Erik Galloway <egalloway@claruscare.com>
 */
export default class EstablishWebSocketConnection extends Task {
	/**
	 * Run the asynchronous task and resolve when complete.
	 *
	 * @param {Function} next
	 * @return {void}
	 */
	async run(next) {
		window.Pusher = Pusher

		const app = this.container.use('App/Foundation/Application')

		app.on('auth:success', this.onAuthSuccess)

		app.on('partner:change', this.onPartnerChange)

		app.on('auth:token', this.onTokenChange)

		app.on('auth:logout', this.onLogout)

		next()
	}

	/**
	 * Close the websocket so a logged-out tab stops receiving call events.
	 *
	 * @return {void}
	 */
	onLogout() {
		const app = Application.getInstance()

		if (!app.container.isBinding('Echo')) {
			return
		}

		try {
			app.use('Echo').disconnect()
		} catch (error) {
			console.error('Failed to disconnect websocket.', error)
		}
	}

	/**
	 * Use the refreshed access token when Pusher next authorizes a private
	 * channel, e.g. when it resubscribes after a reconnect.
	 *
	 * @param {String} token
	 * @return {void}
	 */
	onTokenChange(token) {
		const app = Application.getInstance()

		if (!token || !app.container.isBinding('Echo')) {
			return
		}

		setAuthToken(app.use('Echo'), token)
	}

	/**
	 * Handle the user being successfully authenticated.
	 *
	 * @return {void}
	 */
	onAuthSuccess(app) {
		app.container.singleton('Echo', () => {
			const token = app.store.state.auth.session.access_token

			console.log('Establishing websocket connection.')

			const path = '/api/v3/pusher/authenticate'

			const authEndpoint = process.env.VUE_APP_API_URL
				? process.env.VUE_APP_API_URL + path
				: path

			return new Echo({
				auth: {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
				authEndpoint,
				broadcaster: 'pusher',
				key: process.env.VUE_APP_PUSHER_KEY,
				namespace: false,
			})
		})

		console.log(
			`Listening for events on WS channel: partner_${store?.getters?.partner?.id}`
		)

		app.use('Echo')
			.private(`partner_${store.getters.partner.id}`)
			.listen('new_call', event => app.emit('call:new', event))
			.listen('call_updated', event => app.emit('call:updated', event))
			.listen('partner_migrated', event =>
				app.emit('partner:migrated', event)
			)

		console.log('Listening for OMD releases on WS channel: omd.release')

		app.use('Echo')
			.private('omd')
			.listen('App\\Events\\NewOmdRelease', event =>
				app.emit('omd:updated', event)
			)
	}

	/**
	 * Handle the active partner being changed.
	 *
	 * @param {Number} options.leave
	 * @return {void}
	 */
	onPartnerChange({ leave, subscribe }) {
		const app = Application.getInstance()
		const echo = app.use('Echo')

		// Use latest token from store so Pusher auth succeeds (e.g. after token refresh)
		setAuthToken(echo, store.state.auth.session?.access_token)

		if (leave) {
			console.log(`Leaving WS channel: partner_${leave}`)

			echo.leave(`partner_${leave}`)
		}

		if (subscribe) {
			console.log(
				`Listening for events on WS channel: partner_${subscribe}`
			)

			echo.private(`partner_${subscribe}`)
				.listen('new_call', event => app.emit('call:new', event))
				.listen('call_updated', event =>
					app.emit('call:updated', event)
				)
				.listen('partner_migrated', event =>
					app.emit('partner:migrated', event)
				)
		}
	}
}
